import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, filter, of, ReplaySubject, takeUntil } from 'rxjs';

import { AuthenticationState, coreSelectors } from 'app/core/state';
import STRINGS from 'app/core/strings';
import { AdminService } from 'app/modules/admin/services/admin.service';
import { AuthService } from 'app/services/auth.service';
import { Loggable } from 'app/utils/logging/loggable';

@Component({
  selector: 'landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
  standalone: false,
})
export class LandingComponent extends Loggable implements OnInit, OnDestroy {
  private teamId?: string;
  private isGuest = false;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private store: Store,
    private adminService: AdminService,
  ) {
    super();

    if (this.route.snapshot) {
      this.teamId = this.route.snapshot.paramMap.get('teamId') ?? '';
    }
    // Check for Guest
    this.store
      .select(coreSelectors.isGuestUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isGuest) => (this.isGuest = isGuest));
    // Check for Authentication state
    this.store
      .select(coreSelectors.getAuthenticationState)
      .pipe(filter((state) => state === AuthenticationState.AUTHENTICATED))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.joinTeamResolver();
          const stateURL = this.route.snapshot.queryParamMap.get('return') ?? '';
          if (!this.isGuest && !stateURL) {
            this.router.navigate(['/home']).catch((error) => {
              this.error('Error navigating to home', error);
            });
          }
        },
      });
  }

  public ngOnInit(): void {
    const title = STRINGS.SCOOT_WELCOME_TITLE;
    this.titleService.setTitle(title);
    this.isIE();
  }

  public async signIn(returnURL: string) {
    this.joinTeamResolver();
    await this.authService.startSignInProcess({ reload: false, returnURL });
  }

  public confirmAccount(returnURL: string) {
    this.joinTeamResolver();
    this.router.navigate(['welcome/confirmAccount'], { queryParams: { return: returnURL } }).catch((error) => {
      this.error('Error navigating to confirm account', error);
    });
  }

  public forgotPassword(returnURL: string) {
    this.router.navigate(['welcome/forgotPassword'], { queryParams: { return: returnURL } }).catch((error) => {
      this.error('Error navigating to forgot password', error);
    });
  }

  public isIE(): void {
    if (navigator) {
      if (navigator.userAgent) {
        const userAgent = navigator.userAgent;
        if (userAgent.indexOf('MSIE ') > -1 || userAgent.indexOf('Trident/') > -1) {
          alert('We do not support Internet Explorer. Please use Chrome, Edge, FireFox, or Safari.');
        }
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private joinTeamResolver(): void {
    if (this.teamId) {
      this.adminService.joinTeam(this.teamId).pipe(
        catchError((error: unknown) => {
          this.error('Error joining team', error);
          void this.router.navigate(['/home']);
          return of('');
        }),
      );
    }
  }
}
